import * as React from 'react';

import { useRouter } from 'next/router';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  FormHelperText,
  IconButton,
  Radio,
  TextField,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';

import { Enum } from '@ha/utilities/Enum';

import { RoomAbuseType } from '@kamernet/core/ApiClient/Kamernet';
import { AppServicesContext } from '@kamernet/core/AppServices';
import { reportError } from '@kamernet/core/Errors';
import { useIntl } from '@kamernet/core/Intl';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { CommonStyles } from '@kamernet/styles/CommonStyles';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';

import { RoomAbuseTypeTranslations } from '@kamernet/modules/Listing/translations';

import { ListingDetailsContext } from '../../ListingDetailsContext';

import styles from './ReportListing.module.css';

type ReportListingProps = {
  setOpen: (open: boolean) => void;
};

export const ReportListingContent = ({ setOpen }: ReportListingProps) => {
  const [reportDescription, setReportDescription] = React.useState('');
  const { listingDetails } = ListingDetailsContext.useContext();
  const router = useRouter();
  const [selectedReason, setSelectedReason] = React.useState<RoomAbuseType>();
  const { apiClient } = AppServicesContext.useContext();
  const [hideDescriptionError, setHideDescriptionError] = React.useState(true);
  const [hideReasonError, setHideReasonError] = React.useState(true);
  const [openToastMessage, setOpenToastMessage] = React.useState(false);
  const isDesktop = useMediaIsDesktop();
  const { T, locale } = useIntl();

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(parseInt(event.target.value, 10));
    setHideReasonError(true);
  };

  const handleInputChange = (event: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setReportDescription(event.target.value);
  };

  const handleSubmit = React.useCallback(() => {
    const handleSubmitAsync = async () => {
      try {
        if (!reportDescription || reportDescription.length < 5) {
          setHideDescriptionError(false);
          return;
        }

        if (!selectedReason) {
          setHideReasonError(false);
          return;
        }

        await apiClient.listing.listingAbuse({
          listingAbuseRequest: {
            listingAbuseTypeId: selectedReason,
            listingId: listingDetails.listingId,
            description: reportDescription,
          },
        });

        setOpenToastMessage(true);
        setTimeout(() => {
          router.push(ROUTES.Home.getURL({}, locale)).catch(() => {
            // ignore
          });
        }, 1000);
      } catch (error) {
        setOpenToastMessage(false);

        reportError(
          new Error('Report listing abuse is failed to process', {
            cause: error,
          }),
        );
      }
    };

    handleSubmitAsync().catch(() => {
      // ignore
    });
  }, [
    apiClient.listing,
    listingDetails.listingId,
    locale,
    reportDescription,
    router,
    selectedReason,
  ]);

  return (
    <React.Fragment>
      <div className={isDesktop ? styles.root : styles.drawerContent}>
        <div className={styles.header}>
          <IconButton className={styles.close} onClick={() => setOpen(false)}>
            <CloseIcon fontSize="large" />
          </IconButton>

          <Typography id="modal-modal-title" variant="h3">
            {T('report_listing.heading')}
          </Typography>
        </div>
        <div className={styles.body}>
          <div className={CommonStyles.padding_block_1}>
            <Typography variant="h6" className={CommonStyles.padding_bottom_2}>
              {T('report_listing.intro_why')}
            </Typography>
            <div className={styles.checklist}>
              {Enum.values(RoomAbuseType).map((abuseReason, index) => (
                <label
                  key={abuseReason}
                  className={styles.row}
                  htmlFor={`radio-${index}`}
                >
                  <Radio
                    color="primary"
                    className={CommonStyles.padding_0}
                    value={abuseReason}
                    checked={selectedReason === abuseReason}
                    id={`radio-${index}`}
                    onChange={handleReasonChange}
                  />
                  <Typography>
                    {T(RoomAbuseTypeTranslations[abuseReason as RoomAbuseType])}
                  </Typography>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.description}>
            <FormHelperText>{T('lbl.explanation_required')}</FormHelperText>
            <TextField
              id="input-field-report-listing"
              InputProps={{
                className: styles.inputText,
              }}
              fullWidth
              variant="outlined"
              multiline
              placeholder={T('lbl.empty_state.write_here')}
              value={reportDescription}
              onFocus={() => setHideDescriptionError(true)}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <Alert severity="error" hidden={hideDescriptionError}>
          {T('LBL_REPORT_ABUSE_MIN_CHAR_ERROR')}
        </Alert>
        <Alert severity="error" hidden={hideReasonError}>
          {T('LBL_SELECT_OPTION_REQUIRED')}
        </Alert>
        <div className={isDesktop ? styles.buttons : styles.fixedButtons}>
          <Button fullWidth size="large" onClick={() => setOpen(false)}>
            {T('LBL_CANCEL')}
          </Button>
          <Button
            fullWidth
            variant="destructive"
            size="large"
            onClick={handleSubmit}
          >
            {T('LBL_REVIEW_SUBMIT')}
          </Button>
        </div>
      </div>
      <Snackbar open={openToastMessage} autoHideDuration={6000}>
        <Alert severity="success">{T('report_listing.toast.success')}</Alert>
      </Snackbar>
    </React.Fragment>
  );
};
